import {ReactNode} from 'react';

import {ShoppingDataLayerProduct} from 'types/recommedation-types/recommendations';

export const shoppingIsProductInStock = (product: ShoppingDataLayerProduct): boolean => {
  return product?.totalInventory ? product?.totalInventory >= 1 : false;
};

export const shoppingIs1PMakroStore = (storeCode: string | undefined): boolean =>
  Boolean(storeCode && !storeCode.includes('3P'));

interface DeliveryInfo {
  text: string;
  icon: ReactNode;
  color: string;
  variant: VariantType;
  hasExpressAndNormal: boolean;
}

type VariantType = 'same-day' | 'next-day' | 'pre-order' | 'express' | 'days-min' | 'days-max' | 'undefined';

export const deliveryInfoParser = (markup: string, hasExpressAndNormal: boolean): DeliveryInfo => {
  // Helper function to extract text content and its wrapper
  const extractTextAndWrapper = (str: string): {text: string; color: string} => {
    const regex = /<(\w+)>([^<]+)<\/\1>/;
    const match = str?.match(regex);
    return {
      text: match?.[2] ?? '',
      color: match?.[1] ?? ''
    };
  };

  // Helper function to find icon component
  const extractIcon = (str: string): string => {
    const iconRegex = /<(\w+Icon\w*)\s*\/?>/;
    const match = str?.match(iconRegex);
    return match?.[1] ?? '';
  };

  // Function to determine variant
  const determineVariant = (iconName: string): VariantType => {
    if (['expressDeliveryIconSmallV2', 'expressDeliveryIconSmall'].includes(iconName)) return 'express';
    if (
      [
        'truck3PIcon',
        'truck3PIconSmall',
        'otherDayTruckIconSmall',
        'otherDayTruckIcon',
        'bulkyTruckIconSmall',
        'bulkyTruckIcon'
      ].includes(iconName)
    )
      return 'days-min';
    if (
      [
        '3 Days',
        'nextDayTruckIcon',
        'nextDayTruckIconSmall',
        'otherDayTruckIconSmall',
        'otherDayTruckIcon',
        'nextDayNewTruckIcon',
        'nextDayNewTruckIconSmall'
      ].includes(iconName)
    )
      return 'next-day';
    if (['sameDayTruckIcon', 'sameDayTruckIconSmall', 'sameDayTruckIconSmallV2'].includes(iconName)) return 'same-day';
    if (
      [
        'preOrderIcon',
        'preOrderIconSmall',
        'otherDayTruckIcon',
        'preOrderTruckIcon',
        'preOrderTruckIconSmall'
      ].includes(iconName)
    )
      return 'pre-order';
    return 'undefined';
  };

  const {text, color} = extractTextAndWrapper(markup);
  const icon = extractIcon(markup);
  const variant = determineVariant(icon);

  return {
    text,
    icon,
    color: colorMapper[color] ?? 'default',
    variant,
    hasExpressAndNormal
  };
};

const colorMapper: Record<string, string> = {
  gradientText: 'primaryGradient',
  orangeText: 'warning',
  blueText: 'info',
  redText: 'primary',
  purpleText: 'accent',
  grayText: 'danger'
};
